import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { POSITION_BG, MATURITY_BG } from "@/lib/constants";
import { HelpCircle, ArrowRight, Sparkles } from "lucide-react";

const MODULE_LABELS = {
  themes: "Clinical Themes", adoption: "Adoption Journey", consensus: "Consensus & Controversies",
  feed: "HCP Voice", competitive: "Competitive Intelligence", evidence: "Evidence Landscape",
  dashboard: "Executive Intelligence", strategy: "Strategic Implications",
};

const CATEGORIES = ["All", "Clinical Positioning", "Testing & Diagnostics", "Adoption Barriers", "Evidence Gaps", "Regional Differences", "Strategy"];

const Label = ({ children }) => (
  <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-1">{children}</p>
);

export default function IntelligenceQuestions({ onNavigate, onAskAI }) {
  const [questions, setQuestions] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/questions").then((r) => setQuestions(r.data.questions));
  }, []);

  const visible = cat === "All" ? questions : questions.filter((q) => q.category === cat);

  return (
    <div className="space-y-4" data-testid="questions-view">
      <div className="bg-slate-900 rounded-xl p-6 border border-slate-800">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-teal-400" />
          <h3 className="font-heading text-base font-bold text-white">What this tab helps you answer</h3>
        </div>
        <p className="text-sm text-slate-400 mt-1.5 max-w-4xl">This assessment answers 15 priority intelligence questions. Each answer includes a synthesized position, confidence score, maturity signal and traceable supporting statements, with links to the underlying module.</p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-bold text-slate-900">Priority Intelligence Questions <span className="font-mono text-xs font-normal text-slate-400 ml-1">{visible.length} of {questions.length}</span></h2>
        <div className="flex flex-wrap gap-1.5" data-testid="question-filters">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`question-filter-${c.toLowerCase().replace(/\W+/g, "-")}`}
              onClick={() => setCat(c)}
              className={`text-[11px] font-medium rounded-full px-3 py-1 border transition-colors ${cat === c ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-700"}`}
            >{c}</button>
          ))}
        </div>
      </div>

      <Accordion type="single" collapsible className="space-y-2">
        {visible.map((q) => (
          <AccordionItem key={q.id} value={q.id} className="bg-white border border-slate-200/80 rounded-xl px-5 shadow-sm data-[state=open]:border-teal-400">
            <AccordionTrigger data-testid={`question-trigger-${q.id}`} className="hover:no-underline py-4">
              <div className="flex items-center gap-3 text-left pr-3 w-full">
                <span className="font-mono text-xs text-slate-300 shrink-0">{String(q.order).padStart(2, "0")}</span>
                <span className="text-sm font-semibold text-slate-900 flex-1">{q.question}</span>
                <span className="text-[9px] font-mono uppercase tracking-wide text-slate-400 border border-slate-200 rounded-full px-2 py-0.5 shrink-0 hidden lg:inline">{q.category}</span>
                <span className="font-mono text-xs font-bold text-teal-700 shrink-0 hidden sm:inline">{q.confidence}<span className="text-slate-400 font-normal">/100</span></span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <div className="pl-8 space-y-4">
                <div>
                  <Label>Answer</Label>
                  <p className="text-sm text-slate-700 leading-relaxed">{q.answer}</p>
                </div>
                <div>
                  <Label>Classification</Label>
                  <div className="flex flex-wrap gap-1.5">
                    <Badge variant="outline" className={`text-[10px] ${POSITION_BG[q.dominant_position]}`}>{q.dominant_position}</Badge>
                    <Badge variant="outline" className={`text-[10px] ${MATURITY_BG[q.maturity]}`}>{q.maturity}</Badge>
                    <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">Confidence {q.confidence}/100</Badge>
                    {q.functions.map((f) => <Badge key={f} variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-100">{f}</Badge>)}
                  </div>
                </div>
                <div>
                  <Label>Supporting evidence</Label>
                  <p className="font-mono text-[11px] text-slate-500">{q.insight_ids.join(", ")}</p>
                </div>
                <div className="pt-1 border-t border-slate-100">
                  <Label>Next step</Label>
                  <div className="flex flex-wrap gap-2">
                    <button
                      data-testid={`question-explore-${q.id}`}
                      onClick={() => onNavigate(q.module)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-3 py-1.5 hover:bg-teal-100 transition-colors"
                    >
                      Explore in {MODULE_LABELS[q.module]} <ArrowRight className="w-3 h-3" />
                    </button>
                    <button
                      data-testid={`question-ask-ai-${q.id}`}
                      onClick={() => onAskAI(q.question)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full px-3 py-1.5 hover:bg-indigo-100 transition-colors"
                    >
                      <Sparkles className="w-3 h-3" /> Deeper AI analysis
                    </button>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
}
